import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, User, Calendar, Home, LogOut, MessageCircle, Settings, BarChart3, Heart, Star, Shield } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import LanguageSelector from './LanguageSelector';
import NotificationBell from '../notifications/NotificationBell';

const Header: React.FC = () => {
  const { t } = useTranslation();
  const { isAuthenticated, signOut, user } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    await signOut();
    navigate('/');
    setIsMenuOpen(false);
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const isActiveRoute = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const navItems = isAuthenticated ? [
    { path: '/app', label: t('nav.home'), icon: Home },
    { path: '/app/dashboard', label: t('nav.dashboard'), icon: BarChart3 },
    { path: '/app/calendar', label: t('nav.calendar'), icon: Calendar },
    { path: '/app/ask-doctor', label: 'Ask a Doctor', icon: MessageCircle },
    { path: '/app/profile', label: t('nav.profile'), icon: Settings },
  ] : [];

  return (
    <motion.header 
      className="bg-white/90 backdrop-blur-md shadow-sm sticky top-0 z-40 border-b border-purple-100 safe-area-top"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
    >
      <div className="mobile-container">
        <div className="flex justify-between items-center py-3 sm:py-4">
          <Link to={isAuthenticated ? "/app" : "/"} className="flex items-center space-x-3 group">
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="relative"
            >
              {/* Enhanced logo with feminine touch */}
              <div className="flex items-center space-x-2">
                <motion.div
                  className="w-10 h-10 bg-gradient-to-br from-purple-500 via-pink-500 to-rose-500 rounded-2xl flex items-center justify-center shadow-lg"
                  whileHover={{ rotate: 10, scale: 1.1 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <Heart className="w-6 h-6 text-white" />
                </motion.div>
                <div>
                  <span className="text-xl sm:text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-600 via-pink-500 to-purple-700">
                    THREEDOT
                  </span>
                  <div className="flex items-center space-x-1 -mt-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-2 h-2 text-yellow-400 fill-yellow-400" />
                    ))}
                    <span className="text-xs text-gray-500 ml-1">4.9</span>
                  </div>
                </div>
              </div>
              <motion.div
                className="absolute -bottom-1 left-0 h-0.5 bg-gradient-to-r from-purple-600 to-pink-500"
                initial={{ width: 0 }}
                whileHover={{ width: "100%" }}
                transition={{ duration: 0.3 }}
              />
            </motion.div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {!isAuthenticated && (
              <Link 
                to="/" 
                className={`px-3 py-2 rounded-full transition-all duration-300 text-sm ${
                  isActiveRoute('/') && location.pathname === '/'
                    ? 'bg-purple-100 text-purple-700 font-medium' 
                    : 'text-gray-700 hover:bg-purple-50 hover:text-purple-600'
                }`}
              >
                {t('nav.home')}
              </Link>
            )}
            
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center space-x-2 px-3 py-2 rounded-full transition-all duration-300 text-sm ${
                  isActiveRoute(item.path)
                    ? 'bg-purple-100 text-purple-700 font-medium'
                    : 'text-gray-700 hover:bg-purple-50 hover:text-purple-600'
                }`}
              >
                <item.icon size={14} />
                <span>{item.label}</span>
              </Link>
            ))}
            
            {isAuthenticated ? (
              <div className="flex items-center space-x-3 ml-4">
                <NotificationBell />
                
                {/* User greeting */}
                <div className="hidden xl:flex items-center space-x-2 px-3 py-2 bg-gradient-to-r from-purple-50 to-pink-50 rounded-full border border-purple-100">
                  <div className="w-6 h-6 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                    <span className="text-xs text-white font-medium">
                      {user?.user_metadata?.full_name?.charAt(0) || user?.email?.charAt(0) || 'U'}
                    </span>
                  </div>
                  <span className="text-sm text-gray-700">
                    Hi, {user?.user_metadata?.full_name?.split(' ')[0] || 'there'}!
                  </span>
                </div>
                
                <motion.button 
                  onClick={handleLogout}
                  className="flex items-center space-x-2 px-3 py-2 text-gray-700 hover:bg-red-50 hover:text-red-600 rounded-full transition-all duration-300 text-sm"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <LogOut size={14} />
                  <span>{t('nav.logout')}</span>
                </motion.button>
              </div>
            ) : (
              <div className="flex items-center space-x-3">
                {/* Trust indicators for non-authenticated users */}
                <div className="hidden xl:flex items-center space-x-4 text-xs text-gray-600 mr-4">
                  <div className="flex items-center space-x-1">
                    <Shield className="w-3 h-3 text-green-500" />
                    <span>Secure</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Heart className="w-3 h-3 text-pink-500" />
                    <span>50k+ Users</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Star className="w-3 h-3 text-yellow-500" />
                    <span>4.9 Rating</span>
                  </div>
                </div>

                <Link 
                  to="/auth/login" 
                  className="px-3 py-2 text-gray-700 hover:text-purple-600 rounded-full transition-all duration-300 text-sm"
                >
                  {t('nav.login')}
                </Link>
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Link 
                    to="/auth/register" 
                    className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-500 text-white rounded-full font-medium shadow-lg hover:shadow-xl transition-all duration-300 text-sm"
                  >
                    {t('nav.register')}
                  </Link>
                </motion.div>
              </div>
            )}
            
            <div className="ml-3">
              <LanguageSelector />
            </div>
          </nav>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center space-x-2">
            {isAuthenticated && <NotificationBell />}
            <LanguageSelector />
            <motion.button 
              onClick={toggleMenu} 
              className="p-2 text-gray-600 hover:bg-purple-50 rounded-full transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
              whileTap={{ scale: 0.95 }}
            >
              <AnimatePresence mode="wait">
                {isMenuOpen ? (
                  <motion.div
                    key="close"
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <X size={24} />
                  </motion.div>
                ) : (
                  <motion.div
                    key="menu"
                    initial={{ rotate: 90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: -90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Menu size={24} />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="lg:hidden overflow-hidden"
            >
              <div className="py-4 border-t border-purple-100">
                {/* User greeting for mobile */}
                {isAuthenticated && (
                  <motion.div
                    className="flex items-center space-x-3 px-4 py-3 mb-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-100"
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.1 }}
                  >
                    <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                      <span className="text-sm text-white font-medium">
                        {user?.user_metadata?.full_name?.charAt(0) || user?.email?.charAt(0) || 'U'}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        Welcome back, {user?.user_metadata?.full_name?.split(' ')[0] || 'there'}!
                      </p>
                      <p className="text-sm text-gray-600">{user?.email}</p>
                    </div>
                  </motion.div>
                )}

                <nav className="flex flex-col space-y-1">
                  {!isAuthenticated && (
                    <motion.div
                      initial={{ x: -20, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      transition={{ delay: 0.1 }}
                    >
                      <Link 
                        to="/" 
                        className={`flex items-center space-x-3 px-4 py-3 rounded-xl transition-all duration-300 min-h-[44px] ${
                          isActiveRoute('/') && location.pathname === '/'
                            ? 'bg-purple-100 text-purple-700' 
                            : 'text-gray-700 hover:bg-purple-50'
                        }`}
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <Home size={20} />
                        <span className="text-base">{t('nav.home')}</span>
                      </Link>
                    </motion.div>
                  )}
                  
                  {navItems.map((item, index) => (
                    <motion.div
                      key={item.path}
                      initial={{ x: -20, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      transition={{ delay: 0.1 * (index + 2) }}
                    >
                      <Link 
                        to={item.path}
                        className={`flex items-center space-x-3 px-4 py-3 rounded-xl transition-all duration-300 min-h-[44px] ${
                          isActiveRoute(item.path)
                            ? 'bg-purple-100 text-purple-700'
                            : 'text-gray-700 hover:bg-purple-50'
                        }`}
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <item.icon size={20} />
                        <span className="text-base">{item.label}</span>
                      </Link>
                    </motion.div>
                  ))}
                  
                  {isAuthenticated ? (
                    <motion.div
                      initial={{ x: -20, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      transition={{ delay: 0.1 * (navItems.length + 2) }}
                    >
                      <button 
                        onClick={handleLogout}
                        className="flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-red-50 hover:text-red-600 rounded-xl transition-all duration-300 w-full text-left min-h-[44px]"
                      >
                        <LogOut size={20} />
                        <span className="text-base">{t('nav.logout')}</span>
                      </button>
                    </motion.div>
                  ) : (
                    <div className="space-y-2 pt-2">
                      <motion.div
                        initial={{ x: -20, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ delay: 0.3 }}
                      >
                        <Link 
                          to="/auth/login" 
                          className="block px-4 py-3 text-gray-700 hover:bg-purple-50 rounded-xl transition-all duration-300 text-base min-h-[44px] flex items-center"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          {t('nav.login')}
                        </Link>
                      </motion.div>
                      <motion.div
                        initial={{ x: -20, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ delay: 0.4 }}
                      >
                        <Link 
                          to="/auth/register" 
                          className="block px-4 py-3 bg-gradient-to-r from-purple-600 to-pink-500 text-white rounded-xl font-medium text-center transition-all duration-300 text-base min-h-[44px] flex items-center justify-center"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          {t('nav.register')}
                        </Link>
                      </motion.div>

                      {/* Trust indicators for mobile */}
                      <motion.div
                        className="flex items-center justify-center space-x-4 pt-4 text-xs text-gray-600"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.5 }}
                      >
                        <div className="flex items-center space-x-1">
                          <Shield className="w-3 h-3 text-green-500" />
                          <span>Secure</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Heart className="w-3 h-3 text-pink-500" />
                          <span>50k+ Users</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Star className="w-3 h-3 text-yellow-500" />
                          <span>4.9 Rating</span>
                        </div>
                      </motion.div>
                    </div>
                  )}
                </nav>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
};

export default Header;